"use client";

import Link from "next/link";
import * as React from "react";

import { Card } from "@/components/ui/card";
import { buildBlogPath } from "../services/blog-url";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export type BlogSidebarPost = {
  blogContentId: string;
  title: string;
  publishedAtUtc: string;
};

function getUtcYear(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return "Unknown";
  return String(date.getUTCFullYear());
}

function groupPostsByYear(posts: BlogSidebarPost[]) {
  const map = new Map<string, BlogSidebarPost[]>();
  for (const post of posts) {
    const year = getUtcYear(post.publishedAtUtc);
    const existing = map.get(year);
    if (existing) existing.push(post);
    else map.set(year, [post]);
  }
  return map;
}

export default function BlogPostsTreeClient(props: {
  posts: BlogSidebarPost[];
  currentBlogContentId?: string | null;
  className?: string;
}) {
  const { posts, currentBlogContentId, className } = props;

  const sortedPosts = React.useMemo(() => {
    return [...(posts ?? [])].sort(
      (a, b) => new Date(b.publishedAtUtc).valueOf() - new Date(a.publishedAtUtc).valueOf(),
    );
  }, [posts]);

  const grouped = React.useMemo(() => groupPostsByYear(sortedPosts), [sortedPosts]);

  const years = React.useMemo(() => {
    const values = Array.from(grouped.keys());
    values.sort((a, b) => {
      if (a === "Unknown") return 1;
      if (b === "Unknown") return -1;
      return Number.parseInt(b, 10) - Number.parseInt(a, 10);
    });
    return values;
  }, [grouped]);

  const defaultYear = React.useMemo(() => {
    if (!currentBlogContentId) return years[0];
    const active = sortedPosts.find((p) => p.blogContentId === currentBlogContentId);
    if (!active) return years[0];
    return getUtcYear(active.publishedAtUtc);
  }, [currentBlogContentId, sortedPosts, years]);

  if (!sortedPosts.length) return null;

  return (
    <Card className={"border-stone-200 bg-white " + (className ?? "")}>
      <div className="p-5 sm:p-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-stone-500">
            Blog posts
          </div>
          <div className="mt-1 text-sm text-stone-600">Browse all posts by year.</div>
        </div>

        <div className="mt-4">
          <Accordion type="single" collapsible className="w-full" defaultValue={defaultYear}>
            {years.map((year) => {
              const yearPosts = grouped.get(year) ?? [];
              return (
                <AccordionItem key={year} value={year}>
                  <AccordionTrigger className="text-left">
                    <span className="text-base font-semibold text-stone-900">{year}</span>
                    <span className="ml-2 text-xs text-stone-500">({yearPosts.length})</span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="max-h-[60vh] space-y-2 overflow-auto pr-2">
                      {yearPosts.map((post) => {
                        const isActive = Boolean(currentBlogContentId && post.blogContentId === currentBlogContentId);

                        return (
                          <Link
                            key={post.blogContentId}
                            href={buildBlogPath(post)}
                            className={
                              isActive
                                ? "block text-sm font-semibold text-stone-950 underline"
                                : "block text-sm font-semibold text-stone-800 hover:text-secondary hover:underline"
                            }
                          >
                            {post.title}
                          </Link>
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </div>
    </Card>
  );
}
